import React, { useState, useEffect, useMemo } from 'react';
import './AnswerQuestionsPage.css';
import API_BASE_URL from './config';

const AnswerQuestionsPage = () => {
    // Paper Generation States
    const [paperDuration, setPaperDuration] = useState(1); // in hours
    const [questionsForPaper, setQuestionsForPaper] = useState([]); // Questions selected to be on the paper

    // Filter States
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]); // Moved subject here
    const [chapters, setChapters] = useState([]); // Moved chapter here
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedSubjectId, setSelectedSubjectId] = useState(''); // New order
    const [selectedChapterId, setSelectedChapterId] = useState(''); // New order

    // Question Fetching States
    const [availableQuestions, setAvailableQuestions] = useState([]); // Questions fetched from API based on filters
    const [loadingFilters, setLoadingFilters] = useState({
        classes: true,
        subjects: false, // New order
        chapters: false, // New order
        questions: false,
    });
    const [filterErrors, setFilterErrors] = useState({
        classes: null,
        subjects: null, // New order
        chapters: null, // New order
        questions: null,
    });

    // --- API Fetch Utility Function ---
    const fetchData = async (endpoint, setState, setLoadingState, setErrorState, queryParams = '') => {
        setLoadingState(true);
        setErrorState(null);
        try {
            const url = `${API_BASE_URL}/${endpoint}${queryParams ? `?${queryParams}` : ''}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            // Ensure data is an array before setting state
            if (Array.isArray(data)) {
                setState(data);
                return data;
            } else {
                console.warn(`API for ${endpoint} returned non-array data:`, data);
                setErrorState(`Invalid data format for ${endpoint}.`);
                setState([]);
                return [];
            }
        } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
            setErrorState(`Failed to load ${endpoint}. Please try again.`);
            setState([]); // Clear previous data on error
            return [];
        } finally {
            setLoadingState(false);
        }
    };

    // --- Effects for Filter Data ---

    // 1. Fetch Classes on component mount
    useEffect(() => {
        fetchData(
            'classes',
            setClasses,
            (val) => setLoadingFilters((prev) => ({ ...prev, classes: val })),
            (err) => setFilterErrors((prev) => ({ ...prev, classes: err }))
        ).then(data => {
            if (Array.isArray(data) && data.length > 0) {
                if (data[0] && data[0].class_id !== undefined && data[0].class_id !== null) {
                    setSelectedClassId(data[0].class_id.toString());
                } else {
                    setSelectedClassId('');
                }
            } else {
                setSelectedClassId('');
            }
        });
    }, []);

    // 2. Fetch Subjects when selectedClassId changes
    useEffect(() => {
        if (!selectedClassId) {
            setSubjects([]);
            setSelectedSubjectId('');
            return;
        }
        fetchData(
            'subjects',
            setSubjects,
            (val) => setLoadingFilters((prev) => ({ ...prev, subjects: val })),
            (err) => setFilterErrors((prev) => ({ ...prev, subjects: err })),
            `class_id=${selectedClassId}` // Subjects now depend on class_id
        ).then(data => {
            if (Array.isArray(data) && data.length > 0) {
                // Assuming subjects have subject_id and subject_name
                if (data[0] && data[0].subject_id !== undefined && data[0].subject_id !== null) {
                    setSelectedSubjectId(data[0].subject_id.toString());
                } else {
                    setSelectedSubjectId('');
                }
            } else {
                setSelectedSubjectId('');
            }
        });
    }, [selectedClassId]); // Dependency changed to selectedClassId

    // 3. Fetch Chapters when selectedSubjectId changes
    useEffect(() => {
        if (!selectedSubjectId) { // Chapters now depend on selectedSubjectId
            setChapters([]);
            setSelectedChapterId('');
            return;
        }
        fetchData(
            'chapters',
            setChapters,
            (val) => setLoadingFilters((prev) => ({ ...prev, chapters: val })),
            (err) => setFilterErrors((prev) => ({ ...prev, chapters: err })),
            `subject_id=${selectedSubjectId}` // Chapters now depend on subject_id
        ).then(data => {
            if (Array.isArray(data) && data.length > 0) {
                // Assuming chapters have chapter_id
                if (data[0] && data[0].chapter_id !== undefined && data[0].chapter_id !== null) {
                    setSelectedChapterId(data[0].chapter_id.toString());
                } else {
                    setSelectedChapterId('');
                }
            } else {
                setSelectedChapterId('');
            }
        });
    }, [selectedSubjectId]); // Dependency changed to selectedSubjectId

    // Fetch Questions based on all filters
    useEffect(() => {
        const queryParams = new URLSearchParams();
        if (selectedClassId) queryParams.append('class_id', selectedClassId);
        if (selectedSubjectId) queryParams.append('subject_id', selectedSubjectId); // New order
        if (selectedChapterId) queryParams.append('chapter_id', selectedChapterId); // New order

        fetchData(
            'questions',
            setAvailableQuestions,
            (val) => setLoadingFilters((prev) => ({ ...prev, questions: val })),
            (err) => setFilterErrors((prev) => ({ ...prev, questions: err })),
            queryParams.toString()
        );
    }, [selectedClassId, selectedSubjectId, selectedChapterId]); // Dependencies updated

    // --- Handlers for Paper Generation ---

    const handleDurationChange = (delta) => {
        setPaperDuration((prev) => Math.max(1, prev + delta));
    };

    const handleAddQuestionToPaper = (questionToAdd) => {
        // Use question_id for checks
        if (!questionToAdd || questionToAdd.question_id === undefined || questionToAdd.question_id === null) {
            console.error("Attempted to add an invalid question:", questionToAdd);
            alert('Cannot add an invalid question.');
            return;
        }

        // Prevent adding duplicates using question_id
        if (questionsForPaper.some(q => q.question_id === questionToAdd.question_id)) {
            alert('This question is already added to the paper.');
            return;
        }

        // Augment question with names for easier display in the paper section
        const questionWithNames = {
            ...questionToAdd,
            // Use class_id and class_name from classes array
            class_name: classes.find(cls => cls.class_id?.toString() === questionToAdd.class_id?.toString())?.class_name || '',
            // Use chapter_id and chapter_name from chapters array
            chapter_name: chapters.find(chap => chap.chapter_id?.toString() === questionToAdd.chapter_id?.toString())?.chapter_name || '',
            // Use subject_id and subject_name from subjects array
            subject_name: subjects.find(sub => sub.subject_id?.toString() === questionToAdd.subject_id?.toString())?.subject_name || '',
        };
        setQuestionsForPaper((prev) => [...prev, questionWithNames]);
    };

    const handleRemoveQuestionFromPaper = (questionId) => {
        // Use question_id for removal
        setQuestionsForPaper((prev) => prev.filter(q => q.question_id !== questionId));
    };

    // Calculate total marks for the paper (assuming marks are on the question object, if not, this will be 0)
    const totalMarks = useMemo(() => {
        return questionsForPaper.reduce((sum, q) => {
            let qMarks = q.marks || 0; // Check if marks exist
            if (q.type_name === 'Comprehension' && Array.isArray(q.subQuestions) && q.subQuestions.length > 0) {
                qMarks = q.subQuestions.reduce((subSum, subQ) => subSum + (subQ.marks || 0), 0);
            }
            return sum + qMarks;
        }, 0);
    }, [questionsForPaper]);

    // --- JSX Rendering ---

    const renderQuestionDisplay = (question, index, isAddedToPaper = false) => {
        // Ensure question.question_id is available for key, fallback to index if not
        const key = question.question_id ? question.question_id.toString() : `q-${index}-${isAddedToPaper ? 'paper' : 'available'}`;

        return (
            <div key={key} className={`question-card ${isAddedToPaper ? 'added-question-card' : ''}`}>
                <div style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                    <p className="question-meta mb-1">
                        {/* Use class_name, chapter_name, subject_name directly from the question object */}
                        {question.class_name && `Class: ${question.class_name}`}
                        {question.chapter_name && ` | Chapter: ${question.chapter_name}`}
                        {question.subject_name && ` | Subject: ${question.subject_name}`}
                        {/* Use difficulty_name */}
                        {question.difficulty_name && ` | Difficulty: ${question.difficulty_name}`}
                        {/* Check for marks before displaying */}
                        {question.marks && ` | Marks: ${question.marks}`}
                    </p>

                    {/* Use type_name for checking type */}
                    {question.type_name === 'Comprehension' ? (
                        <div>
                            {/* Use question_text and passage_text */}
                            <p className="question-text mb-1"><b>Q{index + 1} (Comprehension):</b> {question.question_text || 'No title'}</p>
                            <p className="question-passage">{question.passage_text}</p>
                            {/* If your API does provide subQuestions for Comprehension, this part is fine. */}
                            {Array.isArray(question.subQuestions) && question.subQuestions.length > 0 ? (
                                question.subQuestions.map((subQ, subIndex) => (
                                    <div key={subQ.question_id || subIndex} className="sub-question-block">
                                        <p className="question-text mb-1"><b>Q{index + 1}.{subIndex + 1} ({subQ.type_name || 'N/A'}):</b> {subQ.question_text}</p>
                                        {subQ.type_name === 'Multiple Choice Single Correct' && Array.isArray(subQ.question_details) && subQ.question_details.length > 0 && (
                                            <ul className="options-list">
                                                {/* Map over question_details for options */}
                                                {subQ.question_details.map((opt, i) => (
                                                    <li key={opt.detail_id || i} className="option-item">
                                                        {opt.details} {isAddedToPaper && opt.is_correct && <span className="correct-answer">(Correct)</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {!isAddedToPaper && subQ.type_name !== 'Multiple Choice Single Correct' && <p className="answer-input-note mb-1"><i>Answer input not shown for this type here.</i></p>}
                                    </div>
                                ))
                            ) : (
                                // This block handles cases where 'Comprehension' questions might have options directly
                                question.type_name === 'Multiple Choice Single Correct' && Array.isArray(question.question_details) && question.question_details.length > 0 ? (
                                    <ul className="options-list">
                                        {question.question_details.map((opt, i) => (
                                            <li key={opt.detail_id || i} className="option-item">
                                                {opt.details} {isAddedToPaper && opt.is_correct && <span className="correct-answer">(Correct)</span>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="answer-input-note mb-1"><i>No options available or not an MCQ.</i></p>
                                )
                            )}
                        </div>
                    ) : (
                        <div>
                            {/* Use question_text for regular questions */}
                            <p className="question-text mb-1"><b>Q{index + 1} ({question.type_name || 'N/A'}):</b> {question.question_text}</p>
                            {/* Use question_details for options */}
                            {question.type_name === 'Multiple Choice Single Correct' && Array.isArray(question.question_details) && question.question_details.length > 0 ? (
                                <ul className="options-list">
                                    {question.question_details.map((opt, i) => (
                                        <li key={opt.detail_id || i} className="option-item">
                                            {opt.details} {isAddedToPaper && opt.is_correct && <span className="correct-answer">(Correct)</span>}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="answer-input-note mb-1"><i>No options available or not an MCQ.</i></p>
                            )}
                        </div>
                    )}
                </div>
                <div className="question-actions text-end">
                    {!isAddedToPaper ? (
                        <button onClick={() => handleAddQuestionToPaper(question)} className="btn btn-sm btn-primary add-button">
                            Add to Paper
                        </button>
                    ) : (
                        <button onClick={() => handleRemoveQuestionFromPaper(question.question_id)} className="btn btn-sm btn-danger remove-button">
                            {/* <i class="bi bi-x-lg"></i>  */}Remove
                        </button>
                    )}
                </div>
            </div>
        );
    };


    return (
        <div className="answer-questions-page">
            <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
            <li class="breadcrumb-item"><a href="/dashboard/Home">Home</a></li>
            <li class="breadcrumb-item active" aria-current="page">Create Question Paper</li>
        </ol>
    </nav>
        <div className="container px-0">
            {/* Left Panel - Question Selection */}
            <div className="left-panel">
                <h5>Create Question Paper</h5>

                {/* Duration */}
                <div className="section-duration">
                    <div className='mb-2 row d-flex align-items-center'>
                    <label className='col-sm-5'><b>Examination Duration:</b></label>
                    <div className="duration-controls col-sm-7 d-flex align-items-center">
                        <button className='btn btn-sm btn-primary mx-1 my-0 py-1' onClick={() => handleDurationChange(-1)}>-</button>
                        <span>{paperDuration} hour(s)</span>
                        <button className='btn btn-sm btn-primary mx-1 my-0 py-1' onClick={() => handleDurationChange(1)}>+</button>
                    </div>
                    </div>
                </div>

                {/* Class, Subject, Chapter Filters - Reordered JSX */}
                <div className="filters-grid">
                    <div class="mb-2 row w-100 d-flex align-items-center">
                        <label className='col-sm-4' htmlFor=" class-select-answer"><b>Select Class:</b></label>
                        <div class="col-sm-8">
                        <select
                            id="class-select-answer"
                            value={selectedClassId}
                            onChange={(e) => setSelectedClassId(e.target.value)}
                            className="select-input form-control"
                            disabled={loadingFilters.classes}
                        >
                            <option value="">-- All Classes --</option>
                            {filterErrors.classes ? (
                                <option disabled>{filterErrors.classes}</option>
                            ) : (
                                classes.map((cls) => (
                                    <option key={cls.class_id?.toString() || `cls-${Math.random()}`} value={cls.class_id?.toString()}>
                                        {cls.class_name}
                                    </option>
                                ))
                            )}
                        </select>
                        </div>
                        {loadingFilters.classes && <p className="loading-message">Loading classes...</p>}
                    </div>

                    <div class="mb-2 row w-100 d-flex align-items-center">
                        <label className='col-sm-4' htmlFor="subject-select-answer"><b>Select Subject:</b></label> {/* Moved up */}
                        <div class="col-sm-8"> 
                        <select
                            id="subject-select-answer"
                            value={selectedSubjectId}
                            onChange={(e) => setSelectedSubjectId(e.target.value)}
                            className="select-input form-control"
                            disabled={!selectedClassId || loadingFilters.subjects} 
                        >
                            <option value="">-- All Subjects --</option>
                            {filterErrors.subjects ? (
                                <option disabled>{filterErrors.subjects}</option>
                            ) : (
                                subjects.map((sub) => (
                                    <option key={sub.subject_id?.toString() || `sub-${Math.random()}`} value={sub.subject_id?.toString()}>
                                        {sub.subject_name}
                                    </option>
                                ))
                            )}
                        </select>
                        </div>
                        {loadingFilters.subjects && <p className="loading-message">Loading subjects...</p>}
                    </div>

                    <div class="mb-2 row w-100 d-flex align-items-center">
                        <label className='col-sm-4' htmlFor="chapter-select-answer"><b>Select Chapter:</b></label> {/* Moved down */}
                        <div class="col-sm-8"> 
                        <select
                            id="chapter-select-answer"
                            value={selectedChapterId}
                            onChange={(e) => setSelectedChapterId(e.target.value)}
                            className="select-input form-control"
                            disabled={!selectedSubjectId || loadingFilters.chapters} 
                        >
                            <option value="">-- All Chapters --</option>
                            {filterErrors.chapters ? (
                                <option disabled>{filterErrors.chapters}</option>
                            ) : (
                                chapters.map((chap) => (
                                    <option key={chap.chapter_id?.toString() || `chap-${Math.random()}`} value={chap.chapter_id?.toString()}>
                                        {chap.chapter_name}
                                    </option>
                                ))
                            )}
                        </select>
                        </div>
                        {loadingFilters.chapters && <p className="loading-message">Loading chapters...</p>}
                    </div>
                </div>

                {/* Available Questions List */}
                <div className="available-questions-section">
                    <h5>Available Questions ({availableQuestions.length})</h5>
                    {loadingFilters.questions ? (
                        <p className="mb-1 loading-message">Loading questions...</p>
                    ) : filterErrors.questions ? (
                        <p className="mb-1 error-message">{filterErrors.questions}</p>
                    ) : availableQuestions.length === 0 ? (
                        <p className="mb-1 info-message">No questions found for the selected filters.</p>
                    ) : (
                        <div className="questions-list  " > 
                            {availableQuestions.map((q, index) => renderQuestionDisplay(q, index))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Panel - Question Paper Preview */}
            <div className="right-panel">
                <h5 style={{ textAlign: 'center' }}>Question Paper Preview</h5>
                <p><b>Time:</b> {paperDuration} hour(s)</p>
                <p><b>Total Marks:</b> {totalMarks}</p>

                <div className="paper-questions-list">
                    {questionsForPaper.length === 0 ? (
                        <p className="info-message empty-paper-message">
                            Add questions from the left panel to build your question paper.
                        </p>
                    ) : (
                        questionsForPaper.map((q, i) => renderQuestionDisplay(q, i, true))
                    )}
                </div>
            </div>
        </div>
        </div>
    );
};

export default AnswerQuestionsPage;